import { useState } from 'react'
import { useAuth } from '../context/AuthContext'
import { getTransactionsCredencial } from '../services/accountService'
import { format, subDays } from 'date-fns'
import { es } from 'date-fns/locale'
import DatePicker, { registerLocale } from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { LuDownload, LuSheet, LuFileText, LuLoader, LuCircleCheck, LuTriangleAlert } from 'react-icons/lu'
import './LiquidacionesPage.css'

// Registrar locale español para el datepicker
registerLocale('es', es)

// Formatear timestamp sin conversión de zona horaria
// Convierte "YYYY-MM-DD HH:MM:SS" a "DD/MM/YYYY HH:MM"
const formatTimestampSafe = (timestamp) => {
  if (!timestamp || timestamp === '-') return '-'
  
  // Extraer YYYY-MM-DD HH:MM:SS directamente con regex (SIN usar new Date())
  const match = timestamp.match(/^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2})/)
  if (match) {
    const [, year, month, day, hour, minute] = match
    return `${day}/${month}/${year} ${hour}:${minute}`
  }
  
  // Fallback: devolver tal cual
  return timestamp
}

function LiquidacionesPage() {
  const { assignedAccounts, username } = useAuth()
  
  // Selección de cuentas
  const [selectedAccounts, setSelectedAccounts] = useState([]) // Array de account_numbers
  const [selectAll, setSelectAll] = useState(false)
  
  // Fechas - Usamos Date objects para el DatePicker
  const [fromDate, setFromDate] = useState(subDays(new Date(), 7))
  const [toDate, setToDate] = useState(subDays(new Date(), 1))
  
  // Formato
  const [exportFormat, setExportFormat] = useState('csv')
  
  // Modal de confirmación
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  
  // Estado de exportación
  const [isExporting, setIsExporting] = useState(false)
  const [exportLogs, setExportLogs] = useState([])
  const [exportProgress, setExportProgress] = useState({
    currentAccount: 0,
    totalAccounts: 0,
    currentPage: 0,
    totalMovements: 0,
    currentAccountNumber: ''
  })
  const [exportComplete, setExportComplete] = useState(false)
  const [exportError, setExportError] = useState(null)
  const [allMovements, setAllMovements] = useState([])

  // Manejar selección de cuenta individual
  const handleAccountToggle = (accountNumber) => {
    setSelectedAccounts(prev => {
      if (prev.includes(accountNumber)) {
        const newSelected = prev.filter(a => a !== accountNumber)
        setSelectAll(false)
        return newSelected
      } else {
        const newSelected = [...prev, accountNumber]
        if (newSelected.length === assignedAccounts.length) {
          setSelectAll(true)
        }
        return newSelected
      }
    })
  }

  // Manejar selección de todas
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedAccounts([])
      setSelectAll(false)
    } else {
      setSelectedAccounts(assignedAccounts.map(acc => acc.account_number))
      setSelectAll(true)
    }
  }

  // Agregar log
  const addLog = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString('es-AR')
    setExportLogs(prev => [...prev, { timestamp, message, type }])
  }

  // Validar antes de mostrar confirmación
  const handleExportClick = () => {
    if (selectedAccounts.length === 0) {
      alert('Por favor selecciona al menos una cuenta')
      return
    }
    if (!fromDate || !toDate) {
      alert('Por favor selecciona las fechas')
      return
    }
    setShowConfirmModal(true)
  }

  // Iniciar exportación
  const startExport = async () => {
    setShowConfirmModal(false)
    setIsExporting(true)
    setExportComplete(false)
    setExportError(null)
    setExportLogs([])
    setAllMovements([])
    
    const accountsToExport = assignedAccounts.filter(acc => 
      selectedAccounts.includes(acc.account_number)
    )
    
    addLog(`🚀 Iniciando exportación de ${accountsToExport.length} cuenta(s)`, 'info')
    addLog(`📅 Período: ${format(fromDate, 'dd/MM/yyyy')} al ${format(toDate, 'dd/MM/yyyy')}`, 'info')
    addLog(`⚠️ Este proceso puede demorar varios minutos...`, 'warning')
    
    let totalMovements = []
    
    try {
      for (let i = 0; i < accountsToExport.length; i++) {
        const account = accountsToExport[i]
        const accountNumber = account.account_number
        const accountCVU = account.cvu
        
        setExportProgress(prev => ({
          ...prev,
          currentAccount: i + 1,
          totalAccounts: accountsToExport.length,
          currentAccountNumber: accountNumber,
          currentPage: 0
        }))
        
        addLog(`📋 Cuenta ${i + 1}/${accountsToExport.length}: ${accountNumber}`, 'info')
        
        let page = 1
        let hasMore = true
        let accountMovements = []
        
        while (hasMore) {
          setExportProgress(prev => ({
            ...prev,
            currentPage: page
          }))
          
          try {
            addLog(`   📄 Página ${page}...`, 'info')
            
            const response = await getTransactionsCredencial({
              account: accountNumber,
              date_from: format(fromDate, 'yyyy-MM-dd'),
              date_to: format(toDate, 'yyyy-MM-dd'),
              page: page,
              forceAPI: true // Forzar consulta a Credencial, no a DB local
            })
            
            const movements = response.data || []
            
            // Agregar CVU a cada movimiento para identificar la cuenta
            const movementsWithCVU = movements.map(mov => ({
              ...mov,
              _account_number: accountNumber,
              _account_cvu: accountCVU
            }))
            
            accountMovements = [...accountMovements, ...movementsWithCVU]
            
            setExportProgress(prev => ({
              ...prev,
              totalMovements: prev.totalMovements + movements.length
            }))
            
            addLog(`   ✅ ${movements.length} movimientos (total cuenta: ${accountMovements.length})`, 'success')
            
            // Si devuelve menos de 20, es la última página
            if (movements.length < 20) {
              hasMore = false
            } else {
              page++
              // Pausa para no saturar el API
              await new Promise(resolve => setTimeout(resolve, 500))
            }
            
          } catch (err) {
            addLog(`   ❌ Error en página ${page}: ${err.message}`, 'error')
            hasMore = false
          }
        }
        
        addLog(`   📊 Total cuenta ${accountNumber}: ${accountMovements.length} movimientos`, 'success')
        totalMovements = [...totalMovements, ...accountMovements]
        
        // Pausa entre cuentas
        if (i < accountsToExport.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 1000))
        }
      }
      
      setAllMovements(totalMovements)
      addLog(``, 'info')
      addLog(`🎉 ¡Exportación completada!`, 'success')
      addLog(`📊 Total: ${totalMovements.length} movimientos de ${accountsToExport.length} cuenta(s)`, 'success')
      
      // Generar archivo
      if (totalMovements.length > 0) {
        addLog(`📁 Generando archivo ${exportFormat.toUpperCase()}...`, 'info')
        
        if (exportFormat === 'csv') {
          generateCSV(totalMovements)
        } else {
          generatePDF(totalMovements)
        }
        
        addLog(`✅ Archivo descargado exitosamente`, 'success')
      } else {
        addLog(`⚠️ No se encontraron movimientos en el período seleccionado`, 'warning')
      }
      
      setExportComplete(true)
      
    } catch (err) {
      addLog(`❌ Error general: ${err.message}`, 'error')
      setExportError(err.message)
    }
  }

  // Generar CSV
  const generateCSV = (movements) => {
    let csvContent = 'Cuenta,CVU,Fecha,Descripción,Monto\n'
    
    movements.forEach(mov => {
      const cuenta = mov._account_number || ''
      const cvu = mov._account_cvu || ''
      const fecha = formatTimestampSafe(mov.timestamp) // Formatear sin conversión de timezone
      const descripcion = (mov.merchant_name || mov.tx_description || '-').replace(/,/g, ' ').replace(/"/g, "'")
      const monto = mov.amount || 0
      
      csvContent += `${cuenta},${cvu},"${fecha}","${descripcion}",${monto}\n`
    })
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `liquidaciones_${username}_${format(fromDate, 'yyyy-MM-dd')}_${format(toDate, 'yyyy-MM-dd')}.csv`
    link.click()
  }

  // Generar PDF
  const generatePDF = (movements) => {
    const printWindow = window.open('', '_blank')
    
    // Agrupar por cuenta
    const movementsByAccount = {}
    movements.forEach(mov => {
      const key = mov._account_number || 'Sin cuenta'
      if (!movementsByAccount[key]) {
        movementsByAccount[key] = {
          cvu: mov._account_cvu || '',
          movements: []
        }
      }
      movementsByAccount[key].movements.push(mov)
    })
    
    let tablesHTML = ''
    
    Object.entries(movementsByAccount).forEach(([accountNumber, data]) => {
      tablesHTML += `
        <div class="account-section">
          <h3>Cuenta: ${accountNumber}</h3>
          <p class="cvu">CVU: ${data.cvu}</p>
          <table border="1" cellpadding="8" cellspacing="0">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Descripción</th>
                <th style="text-align:right;">Monto</th>
              </tr>
            </thead>
            <tbody>
              ${data.movements.map(mov => `
                <tr>
                  <td>${mov.timestamp ? (() => { const m = mov.timestamp.match(/^(\\d{4})-(\\d{2})-(\\d{2})\\s+(\\d{2}):(\\d{2})/); return m ? m[3]+'/'+m[2]+'/'+m[1]+' '+m[4]+':'+m[5] : mov.timestamp; })() : '-'}</td>
                  <td>${mov.merchant_name || mov.tx_description || '-'}</td>
                  <td style="text-align:right; color: ${parseFloat(mov.amount) >= 0 ? 'green' : 'red'}">
                    $${parseFloat(mov.amount || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                  </td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          <p class="subtotal">Subtotal: ${data.movements.length} movimientos</p>
        </div>
      `
    })
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Liquidaciones - ${username}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { color: #1a1a2e; border-bottom: 2px solid #1a1a2e; padding-bottom: 10px; }
          h3 { color: #1a1a2e; margin-top: 30px; }
          .info { margin-bottom: 20px; color: #666; }
          .account-section { margin-bottom: 40px; page-break-inside: avoid; }
          .cvu { color: #666; font-size: 0.9rem; margin-bottom: 10px; }
          table { width: 100%; border-collapse: collapse; font-size: 12px; }
          th { background: #1a1a2e; color: white; }
          .subtotal { text-align: right; font-weight: bold; margin-top: 10px; }
          @media print {
            .account-section { page-break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <h1>Reporte de Liquidaciones</h1>
        <div class="info">
          <p><strong>Usuario:</strong> ${username}</p>
          <p><strong>Período:</strong> ${format(fromDate, 'dd/MM/yyyy')} al ${format(toDate, 'dd/MM/yyyy')}</p>
          <p><strong>Total de movimientos:</strong> ${movements.length}</p>
          <p><strong>Generado:</strong> ${format(new Date(), 'dd/MM/yyyy HH:mm')}</p>
        </div>
        ${tablesHTML}
      </body>
      </html>
    `)
    
    printWindow.document.close()
    printWindow.print()
  }

  // Cerrar modal de exportación
  const closeExportModal = () => {
    setIsExporting(false)
    setExportLogs([])
    setExportProgress({
      currentAccount: 0,
      totalAccounts: 0,
      currentPage: 0,
      totalMovements: 0,
      currentAccountNumber: ''
    })
    setExportComplete(false)
    setExportError(null)
  }

  return (
    <div className="liquidaciones-container">
      <div className="liquidaciones-header">
        <h1><LuDownload /> Liquidaciones</h1>
        <p className="subtitle">Exportar Movimientos</p>
      </div>

      <div className="liquidaciones-content">
        {/* Selector de Cuentas */}
        <div className="section">
          <h3>1. Seleccionar Cuenta(s)</h3>
          <div className="accounts-selector">
            <label className={`account-option select-all ${selectAll ? 'selected' : ''}`}>
              <input
                type="checkbox"
                checked={selectAll}
                onChange={handleSelectAll}
              />
              <span>Todas las cuentas ({assignedAccounts.length})</span>
            </label>
            
            <div className="accounts-list">
              {assignedAccounts.map(acc => (
                <label 
                  key={acc.account_number} 
                  className={`account-option ${selectedAccounts.includes(acc.account_number) ? 'selected' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={selectedAccounts.includes(acc.account_number)}
                    onChange={() => handleAccountToggle(acc.account_number)}
                  />
                  <div className="account-info">
                    <span className="account-number">{acc.account_number}</span>
                    <span className="account-cvu">{acc.cvu}</span>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Selector de Fechas */}
        <div className="section">
          <h3>2. Seleccionar Período</h3>
          <div className="dates-selector">
            <div className="date-field">
              <label>Desde:</label>
              <DatePicker
                selected={fromDate}
                onChange={(date) => setFromDate(date)}
                dateFormat="dd/MM/yyyy"
                locale="es"
                placeholderText="dd/mm/aaaa"
                maxDate={toDate}
                className="date-input"
              />
            </div>
            <div className="date-field">
              <label>Hasta:</label>
              <DatePicker
                selected={toDate}
                onChange={(date) => setToDate(date)}
                dateFormat="dd/MM/yyyy"
                locale="es"
                placeholderText="dd/mm/aaaa"
                minDate={fromDate}
                maxDate={new Date()}
                className="date-input"
              />
            </div>
          </div>
        </div>

        {/* Selector de Formato */}
        <div className="section">
          <h3>3. Formato de Exportación</h3>
          <div className="format-selector">
            <label className={`format-option ${exportFormat === 'csv' ? 'selected' : ''}`}>
              <input
                type="radio"
                name="format"
                value="csv"
                checked={exportFormat === 'csv'}
                onChange={() => setExportFormat('csv')}
              />
              <LuSheet className="format-icon" />
              <span>CSV (Excel)</span>
            </label>
            <label className={`format-option ${exportFormat === 'pdf' ? 'selected' : ''}`}>
              <input
                type="radio"
                name="format"
                value="pdf"
                checked={exportFormat === 'pdf'}
                onChange={() => setExportFormat('pdf')}
              />
              <LuFileText className="format-icon" />
              <span>PDF</span>
            </label>
          </div>
        </div>

        {/* Botón Exportar */}
        <div className="export-action">
          <button 
            className="btn-export"
            onClick={handleExportClick}
            disabled={selectedAccounts.length === 0}
          >
            <LuDownload /> Exportar Liquidaciones
          </button>
        </div>
      </div>

      {/* Modal de Confirmación */}
      {showConfirmModal && (
        <div className="modal-overlay">
          <div className="modal confirm-modal">
            <div className="modal-header">
              <h3>Confirmar Exportación</h3>
            </div>
            <div className="modal-body">
              <p>Se exportarán los movimientos de:</p>
              
              <div className="confirm-details">
                <div className="confirm-accounts">
                  <strong>{selectedAccounts.length} cuenta(s):</strong>
                  <ul>
                    {selectedAccounts.slice(0, 5).map(acc => (
                      <li key={acc}>{acc}</li>
                    ))}
                    {selectedAccounts.length > 5 && (
                      <li>... y {selectedAccounts.length - 5} más</li>
                    )}
                  </ul>
                </div>
                
                <div className="confirm-period">
                  <strong>Período:</strong> {format(fromDate, 'dd/MM/yyyy')} al {format(toDate, 'dd/MM/yyyy')}
                </div>
                
                <div className="confirm-format">
                  <strong>Formato:</strong> {exportFormat.toUpperCase()}
                </div>
              </div>
              
              <div className="confirm-warning">
                <LuTriangleAlert />
                <p>Este proceso puede demorar varios minutos dependiendo de la cantidad de movimientos.</p>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowConfirmModal(false)}>
                Cancelar
              </button>
              <button className="btn-confirm" onClick={startExport}>
                Confirmar y Exportar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Progreso */}
      {isExporting && (
        <div className="modal-overlay">
          <div className="modal progress-modal">
            <div className="modal-header progress-header">
              <h3>
                {exportComplete ? (
                  <><LuCircleCheck className="icon-success" /> Exportación Completada</>
                ) : (
                  <><LuLoader className="icon-spin" /> Exportando Liquidaciones</>
                )}
              </h3>
            </div>
            <div className="modal-body">
              {!exportComplete && (
                <>
                  <div className="progress-warning-box">
                    <LuTriangleAlert />
                    <p>Este proceso puede demorar dependiendo de la cantidad de cuentas y movimientos a descargar. Por favor, no cierre esta ventana.</p>
                  </div>
                  
                  <div className="progress-info">
                    <div className="progress-row">
                      <span>Cuenta:</span>
                      <strong>{exportProgress.currentAccount} / {exportProgress.totalAccounts}</strong>
                    </div>
                    <div className="progress-row">
                      <span>Cuenta actual:</span>
                      <strong>{exportProgress.currentAccountNumber}</strong>
                    </div>
                    <div className="progress-row">
                      <span>Página:</span>
                      <strong>{exportProgress.currentPage}</strong>
                    </div>
                    <div className="progress-row highlight">
                      <span>Movimientos descargados:</span>
                      <strong>{exportProgress.totalMovements}</strong>
                    </div>
                  </div>
                  
                  <div className="progress-bar-container">
                    <div className="progress-bar-animated"></div>
                  </div>
                </>
              )}
              
              {exportComplete && (
                <div className="export-complete-summary">
                  <div className="complete-icon">🎉</div>
                  <p className="complete-message">Se descargaron <strong>{exportProgress.totalMovements}</strong> movimientos exitosamente.</p>
                </div>
              )}
            </div>
            <div className="modal-footer">
              {exportComplete && (
                <button className="btn-confirm" onClick={closeExportModal}>
                  Cerrar
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default LiquidacionesPage

